import type { SyntheticEvent } from 'react';
import { useMenuOuvert } from '../partage/useMenuOuvert';
import { HABILLER_LA_BULLE, REMETTRE_A_SA_PLACE } from '../partage/vocabulaire';
import { HabitDeLaBulle, type BulleAVetir, type HabitDuProjetPourUneBulle } from './HabitDeLaBulle';
import './MenuDeLHabit.css';

/**
 * Le menu « ⋯ » d'une bulle de la Carte et de la Hiérarchie (chantier habit-des-bulles,
 * modification 05, Igor : « un rapide sur la bulle directement ex 3 petit point en haut a
 * droite ») : trois petits points au survol, en haut à droite ; un clic ouvre un petit menu avec
 * les rangées de l'espace « Habit » de la fiche (`HabitDeLaBulle`, appelé tel quel, en plus
 * petit), sans ouvrir la fiche. Il se ferme à Échap et au clic dehors (`useMenuOuvert`).
 *
 * Un vrai élément, posé à côté du corps de la bulle : un clic sur « ⋯ » ou dans le menu n'entre
 * pas dans la bulle, n'ouvre pas sa fiche, et ne fait pas glisser la carte (le geste s'arrête ici).
 *
 * Sur la Carte seulement, une bulle déplacée à la profondeur affichée y ajoute « Remettre à sa
 * place » (chantier deplacer-les-bulles, ticket 04), sous l'habit : la Carte le donne, la
 * Hiérarchie jamais. Un clic ferme le menu.
 */
export function MenuDeLHabit({
  bulle,
  projet,
  horsDePortee = false,
  onRemettreASaPlace = null,
}: {
  bulle: BulleAVetir;
  projet: HabitDuProjetPourUneBulle;
  /** La bulle ne se touche pas (cachée, pendant qu'on hiérarchise) : ni « ⋯ » ni menu. */
  horsDePortee?: boolean;
  /** Sa place gardée de cette profondeur s'efface (la Carte, bulle fixée) ; `null` : pas de ligne. */
  onRemettreASaPlace?: (() => void) | null;
}) {
  const menu = useMenuOuvert();
  if (horsDePortee) return null;
  const arreter = (evenement: SyntheticEvent) => evenement.stopPropagation();

  return (
    <div
      className={`menu-habit${menu.ouvert ? ' menu-habit--ouvert' : ''}`}
      ref={menu.ref}
      onPointerDown={arreter}
      onClick={arreter}
      onDoubleClick={arreter}
    >
      <button
        type="button"
        className="menu-habit-bouton"
        aria-label={HABILLER_LA_BULLE}
        title={HABILLER_LA_BULLE}
        aria-haspopup="dialog"
        aria-expanded={menu.ouvert}
        onClick={() => menu.setOuvert(!menu.ouvert)}
      >
        <svg width="10" height="3" viewBox="0 0 14 4" aria-hidden="true">
          <circle cx="2" cy="2" r="1.6" />
          <circle cx="7" cy="2" r="1.6" />
          <circle cx="12" cy="2" r="1.6" />
        </svg>
      </button>

      {menu.ouvert && (
        <div className="menu-habit-panneau" role="dialog" aria-label={HABILLER_LA_BULLE}>
          <HabitDeLaBulle bulle={bulle} projet={projet} compact />
          {onRemettreASaPlace && (
            <button
              type="button"
              className="menu-habit-remettre"
              onClick={() => {
                menu.setOuvert(false);
                onRemettreASaPlace();
              }}
            >
              {REMETTRE_A_SA_PLACE}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
